<template>
  <Form
    @submit="addApprovisionnement"
    :validation-schema="approvisionnementSchema"
    :keep-values="valuess"
    class="appro-form"
  >
    <!-- En-tête : informations générales -->
    <div class="card mb-3 border-0 bg-white">
      <div class="card-body p-15 p-sm-20 p-md-25">
        <h4 class="text-black fw-semibold mb-3">Nouvel approvisionnement</h4>
        <div class="row g-3">
          <div class="col-md-3">
            <label class="d-block text-black fw-semibold mb-1">Date</label>
            <Field
              name="dateAppro"
              type="date"
              :value="getCurrentDate()"
              class="form-control shadow-none fs-md-15 text-black"
            />
            <ErrorMessage name="dateAppro" class="text-danger small" />
          </div>
          <div class="col-md-5">
            <label class="d-block text-black fw-semibold mb-1">
              Fournisseur <span class="text-muted fw-normal small">(facultatif)</span>
            </label>
            <Field name="fournisseur" v-slot="{ field }">
              <Multiselect
                :options="fournisseurOptions"
                :searchable="true"
                track-by="label"
                label="label"
                v-model="field.value"
                v-bind="field"
                placeholder="Sélectionner le fournisseur"
              />
            </Field>
            <ErrorMessage name="fournisseur" class="text-danger small" />
          </div>
          <div class="col-md-4">
            <label class="d-block text-black fw-semibold mb-1">Référence</label>
            <Field
              name="refAppro"
              type="text"
              class="form-control shadow-none fs-md-15 text-black"
              placeholder="Ex. BL-0042"
            />
            <ErrorMessage name="refAppro" class="text-danger small" />
          </div>
        </div>
        <!-- Calculé automatiquement, affiché dans le récapitulatif -->
        <Field name="montantTotal" type="hidden" v-model="montantTotal" />
      </div>
    </div>

    <!-- Une carte par magasin -->
    <div class="card mb-3 border-0 bg-white magasin-card" v-for="(magasin, magasinIndex) in magasins" :key="magasinIndex">
      <div class="magasin-card-header d-flex flex-wrap align-items-center gap-3">
        <span class="magasin-num">{{ magasinIndex + 1 }}</span>
        <div class="flex-grow-1 magasin-select">
          <Multiselect
            :options="magasinOptionsPour(magasinIndex)"
            :searchable="true"
            track-by="label"
            label="label"
            v-model="magasin.magasin"
            placeholder="Choisir le magasin à approvisionner"
            @select="selectMag(magasin.magasin, magasin)"
          />
          <div class="text-danger small" v-if="tentative && magasin.magasin === ''">
            Le magasin est obligatoire.
          </div>
        </div>
        <span class="text-muted small">
          {{ magasin.produits.length }} produit{{ magasin.produits.length > 1 ? 's' : '' }}
          · <strong class="text-black">{{ formatMontant(sousTotal(magasin)) }}</strong>
        </span>
        <button
          v-if="magasins.length > 1"
          type="button"
          class="btn btn-link text-danger p-1"
          title="Retirer ce magasin"
          @click="removeRowMagasin(magasinIndex)"
        >
          <i class="fa fa-trash-o fs-5"></i>
        </button>
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0 produits-table">
          <thead>
            <tr>
              <th class="col-num">#</th>
              <th>Produit <span class="text-danger">*</span></th>
              <th class="col-qte">Quantité <span class="text-danger">*</span></th>
              <th class="col-prix">Prix de revient</th>
              <th class="col-montant text-end">Montant</th>
              <th class="col-action"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(produit, produitIndex) in magasin.produits" :key="produitIndex">
              <td class="text-muted">{{ produitIndex + 1 }}</td>
              <td>
                <Multiselect
                  :options="produitOptionsPour(magasin, produitIndex)"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  v-model="produit.produit"
                  placeholder="Sélectionner le produit"
                  @select="selectProd(produit.produit, produit)"
                />
                <div class="text-danger small" v-if="tentative && produit.produit === ''">
                  Le produit est obligatoire.
                </div>
              </td>
              <td>
                <input
                  type="number"
                  min="1"
                  v-model="produit.qtite"
                  :class="['form-control shadow-none text-black', { 'is-invalid': tentative && validateRowProduit(produit.qtite) }]"
                  placeholder="0"
                />
                <div class="invalid-feedback" v-if="tentative && validateRowProduit(produit.qtite)">
                  Quantité obligatoire.
                </div>
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  v-model="produit.prixRevient"
                  class="form-control shadow-none text-black"
                  placeholder="0"
                />
              </td>
              <td class="text-end fw-semibold text-black text-nowrap">
                {{ formatMontant(produit.montant) }}
              </td>
              <td class="text-center">
                <button
                  type="button"
                  class="btn btn-link text-muted p-1 btn-retirer"
                  title="Retirer ce produit"
                  :disabled="magasin.produits.length === 1"
                  @click="removeRowProduit(magasinIndex, produitIndex)"
                >
                  <i class="fa fa-times"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="px-3 py-2">
        <button type="button" class="btn btn-link p-0 fw-semibold lien-ajout" @click="addRowProduit(magasinIndex)">
          <i class="fa fa-plus me-1"></i>Ajouter un produit
        </button>
      </div>
    </div>

    <button
      type="button"
      class="btn w-100 mb-3 ajout-magasin"
      :disabled="magasins.length >= magasinOptions.length"
      @click="addRowMagasin()"
    >
      <i class="fa fa-plus-circle me-1"></i>Approvisionner un autre magasin
    </button>

    <!-- Récapitulatif collé en bas -->
    <div class="recap-bar d-flex flex-wrap align-items-center justify-content-between gap-3">
      <div>
        <div class="text-muted small">
          {{ magasins.length }} magasin{{ magasins.length > 1 ? 's' : '' }} · {{ nbProduits }} produit{{ nbProduits > 1 ? 's' : '' }}
        </div>
        <div class="text-danger small" v-if="tentative && messageManquant">{{ messageManquant }}</div>
      </div>
      <div class="d-flex flex-wrap align-items-center gap-3">
        <div class="text-end">
          <div class="text-muted small text-uppercase">Total</div>
          <div class="recap-total">{{ formatMontant(montantTotal) }}</div>
        </div>
        <router-link to="/approvisionnements/liste-approvisionnements" class="btn btn-light border">
          Annuler
        </router-link>
        <button class="btn btn-primary" type="submit" :disabled="enCours">
          <span v-if="enCours" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="fa fa-check me-1"></i>Enregistrer
        </button>
      </div>
    </div>
  </Form>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted, reactive, computed } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { error, success } from "@/utils/utils";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import { Approvisionnement } from "@/models/Approvisionnement";

import * as Yup from "yup";
import Multiselect from "@vueform/multiselect";

export default defineComponent({
  name: "AddApprovisionnement",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup: () => {
    const approvisionnementSchema = Yup.object().shape({
      refAppro: Yup.string().notRequired(),
      montantTotal: Yup.number()
        .typeError("Veuillez entrer un nombre")
        .required("Le montant est obligatoire"),
      dateAppro: Yup.date().required("La date est obligatoire"),
      fournisseur: Yup.string().nullable().notRequired(),
    });

    const approvisionnementForm = ref<Approvisionnement | null>(null);
    const router = useRouter();
    const route = useRoute();
    const fournisseurOptions = ref([]);
    const magasinOptions = ref([]);
    const produitOptions = ref([]);
    const montantTotal = ref(0);
    // Passe à true à la première tentative d'enregistrement : les erreurs des lignes s'affichent alors
    const tentative = ref(false);
    const enCours = ref(false);

    const nouvelleLigneProduit = () => ({
      produit: '',
      prixRevient: 0,
      qtite: 0,
      montant: 0,
    });

    const magasins = reactive([
      {
        magasin: '',
        nomMagasin: '',
        produits: [nouvelleLigneProduit()],
      },
    ]);

    watch(
      magasins,
      (newVal) => {
        newVal.forEach((magasin) => {
          magasin.produits.forEach((produit) => {
            produit.montant = (Number(produit.qtite) || 0) * (Number(produit.prixRevient) || 0);
          });
        });

        montantTotal.value = newVal
          .flatMap((mag) => mag.produits)
          .reduce((total, produit) => total + (Number(produit.montant) || 0), 0);
      },
      { deep: true }
    );

    const formatMontant = (montant) => (Number(montant) || 0).toLocaleString('fr-FR') + " FCFA";

    const sousTotal = (magasin) =>
      magasin.produits.reduce((total, produit) => total + (Number(produit.montant) || 0), 0);

    const nbProduits = computed(() =>
      magasins.reduce((total, magasin) => total + magasin.produits.length, 0)
    );

    const validateRowProduit = (e) => {
      return e === '' || e === 0 || e === '0' || e == null || e < 0;
    };

    const messageManquant = computed(() => {
      if (magasins.some((magasin) => magasin.magasin === '')) return "Choisissez un magasin pour chaque carte.";
      if (magasins.some((magasin) => magasin.produits.some((p) => p.produit === ''))) return "Sélectionnez un produit sur chaque ligne.";
      if (magasins.some((magasin) => magasin.produits.some((p) => validateRowProduit(p.qtite)))) return "Renseignez une quantité sur chaque ligne.";
      return '';
    });

    // Un magasin déjà choisi dans une autre carte n'est plus proposé
    const magasinOptionsPour = (magasinIndex) => {
      const dejaPris = magasins
        .filter((_, index) => index !== magasinIndex)
        .map((magasin) => magasin.magasin);
      return magasinOptions.value.filter((option: any) => !dejaPris.includes(option.value));
    };

    // Un produit déjà présent dans le magasin n'est plus proposé sur ses autres lignes
    const produitOptionsPour = (magasin, produitIndex) => {
      const dejaPris = magasin.produits
        .filter((_, index) => index !== produitIndex)
        .map((produit) => produit.produit);
      return produitOptions.value.filter((option: any) => !dejaPris.includes(option.value));
    };

    const addRowMagasin = () => {
      magasins.push({
        magasin: '',
        nomMagasin: '',
        produits: [nouvelleLigneProduit()],
      });
    };

    const addRowProduit = (magasinIndex) => {
      magasins[magasinIndex].produits.push(nouvelleLigneProduit());
    };

    const removeRowMagasin = (index) => {
      magasins.splice(index, 1);
    };

    const removeRowProduit = (magasinIndex, produitIndex) => {
      magasins[magasinIndex].produits.splice(produitIndex, 1);
    };

    const selectMag = (selectedMagasin, magasin) => {
      magasin.magasin = selectedMagasin;
      magasin.nomMagasin = selectedMagasin.split("|")[1];
    };

    const selectProd = (selectedProduit, produit) => {
      produit.produit = selectedProduit;
      produit.prixRevient = Number(selectedProduit.split("|")[2]) || 0;
      produit.qtite = 0;
    };

    const valuess = ref();

    const fetchMagasins = async () => {
      try {
        const response = await ApiService.get("/all/all/magasins");
        const magasinsData = response.data.data;
        magasinOptions.value = magasinsData.map((magasin) => ({
          value: magasin.id + "|" + magasin.nomMagasin,
          label: `${magasin.nomMagasin}`,
        }));
      } catch (error) {
        //
      }
    };

    const fetchTransfert = async (id: string) => {
      try {
        const response = await ApiService.get(`/transferts/${id}`);
        const transfertData = response.data.data;

        magasins[0].produits = transfertData.transfertprocondis.map((item) => {
          const option = produitOptions.value.find(
            (opt) => opt.value.split('|')[0] == item.produitcondi.id
          );

          return {
            produit: option ? option.value : '',
            prixRevient: item.prixUnitaire ?? item.produitcondi.prixVenteHt,
            qtite: item.qtite,
            montant: (item.prixUnitaire ?? item.produitcondi.prixVenteHt) * item.qtite,
          };
        });
      } catch (err) {
        error('Erreur lors du chargement du transfert');
      }
    };

    const fetchProduits = async () => {
      try {
        const response = await ApiService.get("all/produitconditionnements");
        const produitsData = response.data.data;
        produitOptions.value = produitsData.map((produitconditionnements) => ({
          value: produitconditionnements.id + "|" + `${produitconditionnements.produit.nomProd} [ ${produitconditionnements.conditionnement.libelle} ]` + "|" + produitconditionnements.prixVenteHt + "|" + produitconditionnements.prixVenteTtc,
          label: `${produitconditionnements.produit.nomProd} [ ${produitconditionnements.conditionnement.libelle} ]`,
        }));
      } catch (error) {
        //
      }
    };

    const getCurrentDate = () => {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const year = today.getFullYear();
      return `${year}-${month}-${day}`;
    };

    const addApprovisionnement = async (values, { resetForm }) => {
      tentative.value = true;
      if (messageManquant.value) {
        return;
      }

      values.magasins = magasins.map((magasin) => ({
        magasin: magasin.magasin.split('|')[0],
        produits: magasin.produits.map((produit) => ({
          produit: produit.produit.split('|')[0],
          prixRevient: produit.prixRevient,
          qteCondAppro: Number(produit.qtite) || 0,
          montantCond: Number(produit.montant) || 0,
          nomProd: produit.produit.split('|')[1],
        })),
      }));

      enCours.value = true;
      ApiService.post("/approvisionnements", values)
        .then(({ data }) => {
          if (data.code == 201) {
            success(data.message);
            resetForm();
            router.push({ name: "ListeApprovisionnement" });
          }
        })
        .catch(({ response }) => {
          error(response.data.message);
        })
        .finally(() => {
          enCours.value = false;
        });
    };

    const fetchFournisseur = async () => {
      try {
        const response = await ApiService.get("all/fournisseurs");
        const fournisseurData = response.data.data.data;
        fournisseurOptions.value = fournisseurData.map((fournisseur) => ({
          value: fournisseur.id,
          label: `${
            fournisseur.nomFournisseur
              ? fournisseur.nomFournisseur
              : fournisseur.denomination
          }`,
        }));
      } catch (error) {
        //
      }
    };

    onMounted(async () => {
      const transfertId = route.query.id as string;
      fetchFournisseur();
      fetchMagasins();
      await fetchProduits();

      if (transfertId) {
        fetchTransfert(transfertId);
      }
    });

    return {
      approvisionnementSchema,
      addApprovisionnement,
      approvisionnementForm,
      fournisseurOptions,
      magasins,
      removeRowMagasin,
      removeRowProduit,
      addRowMagasin,
      addRowProduit,
      validateRowProduit,
      valuess,
      produitOptions,
      selectProd,
      selectMag,
      getCurrentDate,
      magasinOptions,
      magasinOptionsPour,
      produitOptionsPour,
      montantTotal,
      sousTotal,
      nbProduits,
      messageManquant,
      tentative,
      enCours,
      formatMontant,
      fetchTransfert,
    };
  },
});
</script>

<style scoped>
.magasin-card {
  border-left: 4px solid var(--theme-deafult) !important;
  overflow: visible;
}
.magasin-card-header {
  padding: 14px 16px;
  border-bottom: 1px solid #eef0f4;
}
.magasin-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--theme-deafult);
  color: #fff;
  font-weight: 600;
  flex-shrink: 0;
}
.magasin-select {
  min-width: 220px;
}
.produits-table th {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6c757d;
  font-weight: 600;
  background: #f8f9fb;
  border-bottom: 1px solid #eef0f4;
  white-space: nowrap;
}
.produits-table td {
  border-bottom: 1px solid #f1f2f5;
  vertical-align: top;
}
.produits-table td:first-child,
.produits-table td:nth-child(5),
.produits-table td:last-child {
  padding-top: 14px;
}
.col-num { width: 40px; }
.col-qte { width: 130px; }
.col-prix { width: 160px; }
.col-montant { width: 160px; }
.col-action { width: 48px; }
.btn-retirer:hover:not(:disabled) {
  color: #dc3545 !important;
}
.lien-ajout {
  color: var(--theme-deafult);
  text-decoration: none;
}
.ajout-magasin {
  border: 2px dashed #c9cdd6;
  color: var(--theme-deafult);
  background: transparent;
  padding: 14px;
  font-weight: 600;
}
.ajout-magasin:hover:not(:disabled) {
  border-color: var(--theme-deafult);
  background: rgba(115, 102, 255, 0.05);
}
.recap-bar {
  position: sticky;
  bottom: 0;
  z-index: 5;
  background: #fff;
  border-radius: 8px;
  padding: 14px 20px;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);
  border-top: 3px solid var(--theme-deafult);
}
.recap-total {
  font-size: 24px;
  font-weight: 700;
  color: var(--theme-deafult);
  line-height: 1.1;
  white-space: nowrap;
}
</style>
